import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { UseToast } from '../../hooks/UseToast';
import Button from '../../components/ui/Button';
import { FiSave, FiX, FiUpload, FiImage } from 'react-icons/fi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = UseToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null); // ✅ File store pannu

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    client: '',
    budget: '',
    startDate: '',
    endDate: '',
    description: '',
    status: 'In Progress',
    liveUrl: '',
    githubUrl: '',
    image: ''
  });

  useEffect(() => {
    if (id) fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setFetching(true);
    try {
      const res = await fetch(`${API_URL}/projects/${id}`);
      const data = await res.json();

      if (data.success) {
        const p = data.data;
        setFormData({
          title: p.title || '',
          category: p.category || '',
          client: p.client || '',
          budget: p.budget || '',
          startDate: p.startDate ? p.startDate.split('T')[0] : '',
          endDate: p.endDate ? p.endDate.split('T')[0] : '',
          description: p.description || '',
          status: p.status || 'In Progress',
          liveUrl: p.liveUrl || '',
          githubUrl: p.githubUrl || '',
          image: p.image || ''
        });
        setImagePreview(p.image || '');
      } else {
        toast.error('Project not found');
        navigate('/projects');
      }
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/projects');
    }
    setFetching(false);
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, image: url });
    setImagePreview(url);
    setImageFile(null); // URL use pannum bodhu file clear
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ 5MB check - correct calculation
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error(`Image size should be less than 5MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setFormData({ ...formData, image: '' });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const url = id ? `${API_URL}/projects/${id}` : `${API_URL}/projects`;

      // ✅ FormData use pannu - file upload ku
      const submitData = new FormData();

      // Text fields append
      Object.keys(formData).forEach(key => {
        if (formData[key] && key !== 'image') {
          submitData.append(key, formData[key]);
        }
      });

      // Image file irundha append pannu
      if (imageFile) {
        submitData.append('image', imageFile);
      } else if (formData.image && !formData.image.startsWith('data:')) {
        // URL irundha
        submitData.append('image', formData.image);
      }

      const res = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // ✅ Content-Type set panna koodadhu - browser auto set pannum
        },
        body: submitData
      });

      const data = await res.json();

      if (data.success) {
        toast.success(id ? 'Project updated!' : 'Project created!');
        navigate('/projects');
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error(error);
    }
    setLoading(false);
  };

  if (fetching) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <>
      <style>{`
.form-card { background: #FFF; border-radius: 16px; padding: 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); max-width: 900px; }
.form-title { font-size: 24px; font-weight: 800; color: #0F172A; margin: 0 0 30px 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 20px; }
.form-group { margin-bottom: 20px; }
.form-group-full { grid-column: 1 / -1; }
.form-label { display: block; font-size: 14px; font-weight: 600; color: #334155; margin-bottom: 8px; }
.form-input,.form-select,.form-textarea { width: 100%; padding: 12px 16px; border: 2px solid #E2E8F0; border-radius: 10px; font-size: 15px; transition: all 0.3s ease; box-sizing: border-box; font-family: inherit; }
.form-input:focus,.form-select:focus,.form-textarea:focus { outline: none; border-color: #3B82F6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
.form-textarea { min-height: 120px; resize: vertical; }
.form-actions { display: flex; gap: 12px; justify-content: flex-end; margin-top: 30px; }
.image-upload-section { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
.image-preview { width: 100%; height: 200px; border: 2px dashed #E2E8F0; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: #F8FAFC; overflow: hidden; }
.image-preview img { width: 100%; height: 100%; object-fit: cover; }
.image-preview-placeholder { text-align: center; color: #94A3B8; }
.image-preview-placeholder svg { font-size: 48px; margin-bottom: 8px; }
.upload-btn-wrapper { position: relative; overflow: hidden; display: inline-block; width: 100%; }
.upload-btn { border: 2px dashed #3B82F6; color: #3B82F6; background: #EFF6FF; padding: 12px 20px; border-radius: 10px; font-size: 14px; font-weight: 600; cursor: pointer; width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.3s ease; }
.upload-btn:hover { background: #DBEAFE; }
.upload-btn-wrapper input[type=file] { font-size: 100px; position: absolute; left: 0; top: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }
.or-divider { text-align: center; color: #94A3B8; font-size: 13px; margin: 16px 0; }
        @media (max-width: 768px) {
        .form-grid,.image-upload-section { grid-template-columns: 1fr; }
        }
      `}</style>
      <div>
        <div className="form-card">
          <h1 className="form-title">{id ? 'Edit Project' : 'Add New Project'}</h1>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Project Title *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select Category</option>
                  <option>Web Development</option>
                  <option>Mobile App</option>
                  <option>UI/UX Design</option>
                  <option>Cyber Security</option>
                  <option>E-commerce</option>
                  <option>Custom Software</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Budget (₹)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option>In Progress</option>
                  <option>Completed</option>
                  <option>On Hold</option>
                  <option>Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Live URL</label>
                <input
                  type="url"
                  className="form-input"
                  placeholder="https://..."
                  value={formData.liveUrl}
                  onChange={(e) => setFormData({ ...formData, liveUrl: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group-full">
              <label className="form-label">Project Image</label>
              <div className="image-upload-section">
                <div>
                  <div className="image-preview">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" />
                    ) : (
                      <div className="image-preview-placeholder">
                        <FiImage />
                        <p>No image selected</p>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="upload-btn-wrapper">
                    <button type="button" className="upload-btn">
                      <FiUpload /> Upload Image (Max 5MB)
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </div>
                  <div className="or-divider">OR</div>
                  <input
                    type="url"
                    className="form-input"
                    placeholder="Paste image URL here..."
                    value={formData.image.startsWith('data:') ? '' : formData.image}
                    onChange={handleImageUrlChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">GitHub URL</label>
              <input
                type="url"
                className="form-input"
                placeholder="https://github.com/..."
                value={formData.githubUrl}
                onChange={(e) => setFormData({ ...formData, githubUrl: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/projects')}
                icon={<FiX />}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" icon={<FiSave />} disabled={loading}>
                {loading ? 'Saving...' : id ? 'Update' : 'Create'} Project
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProjectForm;